import React, { useState } from 'react';
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import {
  Eye,
  UserPlus,
  Phone,
  MapPin,
  Star,
} from 'lucide-react';
import {
  ContextMenu,
  ContextMenuButton,
} from '../../../ui/components/Common/ContextMenu';
import { MenuItemType } from '../../../ui/components/Common/ContextMenu/ContextMenu';
import Pagination from '../../../ui/components/Common/DataTable/Paginaion';
import { NameEmailAvatar } from '../../../ui/components/Common/NameEmailAvatar';
import { CandidateType } from '../constants/mockData';

interface CandidateTableProps {
  candidates: CandidateType[];
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  getMatchColor: (percentage: number) => string;
  selectedCandidates?: string[];
  onCandidateSelect?: (candidateId: string) => void;
  onSelectAll?: () => void;
  onViewProfile?: (candidate: CandidateType) => void;
  onInvite?: (candidate: CandidateType) => void;
  currentPage: number;
  totalPages: number;
  totalCandidates: number;
  candidatesPerPage: number;
  onPageChange: (page: number) => void;
}

export const CandidateTable: React.FC<CandidateTableProps> = ({
  candidates,
  getMatchColor,
  selectedCandidates = [],
  onCandidateSelect,
  onSelectAll,
  onViewProfile,
  onInvite,
  currentPage,
  totalPages,
  totalCandidates,
  candidatesPerPage,
  onPageChange,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<CandidateType | null>(null);

  const allSelected =
    candidates.length > 0 && selectedCandidates.length === candidates.length;
  const isIndeterminate =
    selectedCandidates.length > 0 &&
    selectedCandidates.length < candidates.length;

  const menuItems: MenuItemType<CandidateType>[] = [
    {
      label: 'View Profile',
      icon: <Eye size={16} />,
      permission: [],
      onClick: (row) => onViewProfile?.(row),
    },
    {
      label: 'Add to Invite List',
      icon: <UserPlus size={16} />,
      permission: [],
      onClick: (row) => onInvite?.(row),
    },
    {
      label: 'View Contact Info',
      icon: <Phone size={16} />,
      permission: [],
      onClick: () => {},
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {onCandidateSelect && (
                <TableCell padding="checkbox" sx={{ bgcolor: 'grey.50', pl: 1.5 }}>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={isIndeterminate}
                    onChange={onSelectAll}
                  />
                </TableCell>
              )}
              <TableCell sx={{ bgcolor: 'grey.50', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                Candidate
              </TableCell>
              <TableCell sx={{ bgcolor: 'grey.50', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                Skills
              </TableCell>
              <TableCell sx={{ bgcolor: 'grey.50', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                Location
              </TableCell>
              <TableCell align="center" sx={{ bgcolor: 'grey.50', width: 90, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                Match
              </TableCell>
              <TableCell align="center" sx={{ bgcolor: 'grey.50', width: 120, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                Experience (Yrs)
              </TableCell>
              <TableCell sx={{ bgcolor: 'grey.50', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                Invite Lists
              </TableCell>
              <TableCell sx={{ bgcolor: 'grey.50', width: 44 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {candidates.map((candidate) => {
              const isSelected = selectedCandidates.includes(candidate.id);
              return (
                <TableRow
                  key={candidate.id}
                  hover
                  onClick={() => onViewProfile?.(candidate)}
                  selected={isSelected}
                  sx={{ cursor: 'pointer' }}
                >
                  {/* Checkbox */}
                  {onCandidateSelect && (
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCandidateSelect(candidate.id);
                      }}
                      sx={{ pl: 1.5 }}
                    >
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => {}}
                      />
                    </TableCell>
                  )}

                  {/* Candidate */}
                  <TableCell sx={{ minWidth: 200 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <NameEmailAvatar
                        name={candidate.name}
                        email={candidate.email}
                        avatar={candidate.avatar}
                        avatarWidth="32px"
                        avatarHeight="32px"
                        nameFontSize="13px"
                        emailFontSize="11px"
                      />
                      <Box component="span" title="AI Verified" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Star size={13} color="#9333ea" />
                      </Box>
                    </Stack>
                  </TableCell>

                  {/* Skills */}
                  <TableCell sx={{ minWidth: 160 }}>
                    <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
                      {candidate.skills.slice(0, 4).map((skill, i) => (
                        <Chip
                          key={i}
                          label={skill}
                          size="small"
                          sx={{
                            fontSize: '0.6875rem',
                            height: 20,
                            bgcolor: 'grey.100',
                            borderColor: 'grey.200',
                          }}
                        />
                      ))}
                      {candidate.skills.length > 2 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                          +{candidate.skills.length - 2} more
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>

                  {/* Location */}
                  <TableCell sx={{ minWidth: 140 }}>
                    {candidate.location ? (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <MapPin size={12} color="#64748b" />
                        <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {candidate.location}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
                    )}
                  </TableCell>

                  {/* Match */}
                  <TableCell align="center" sx={{ width: 90 }}>
                    <Box
                      className={getMatchColor(Math.round(candidate.score * 100))}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        color: 'common.white',
                        fontWeight: 700,
                        fontSize: '0.6875rem',
                      }}
                    >
                      {Math.round(candidate.score * 100)}%
                    </Box>
                  </TableCell>

                  {/* Experience */}
                  <TableCell align="center" sx={{ width: 120 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      {Math.floor(candidate.experience)}
                    </Typography>
                  </TableCell>

                  {/* Invite Lists */}
                  <TableCell sx={{ minWidth: 140 }}>
                    {candidate.inviteLists && candidate.inviteLists.length > 0 ? (
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {candidate.inviteLists.slice(0, 2).map((list, i) => (
                          <Chip
                            key={i}
                            label={list}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase' }}
                          />
                        ))}
                        {candidate.inviteLists.length > 2 && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                            +{candidate.inviteLists.length - 2}
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
                    )}
                  </TableCell>

                  {/* Context Menu */}
                  <TableCell sx={{ width: 44, px: 1 }} onClick={(e) => e.stopPropagation()}>
                    <ContextMenuButton
                      row={candidate}
                      onOpenMenu={(e, row) => {
                        setMenuAnchor(e.currentTarget);
                        setMenuRow(row as CandidateType);
                      }}
                      onCloseMenu={() => {
                        setMenuAnchor(null);
                        setMenuRow(null);
                      }}
                      anchorEl={menuAnchor}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {menuAnchor && menuRow && (
        <ContextMenu
          anchorEl={menuAnchor}
          onClose={() => {
            setMenuAnchor(null);
            setMenuRow(null);
          }}
          row={menuRow}
          menuItems={menuItems}
        />
      )}

      {/* Pagination */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'grey.200', bgcolor: 'background.paper' }}>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={totalCandidates}
          rowsPerPage={candidatesPerPage}
          handlePageChange={onPageChange}
        />
      </Box>
    </Paper>
  );
};
